import { booleanAttribute } from "#ce/utils/props";
import { DEFAULT_DISTANCES } from "#ce/apps/shared/filters/distance/composables/useDistanceFilter";

export const props = Object.freeze({
  modelValue: {
    type: Object,
    default: undefined,
  },
  lat: {
    type: [Number, String],
    default: undefined,
  },
  lng: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * The distance options (in miles) offered by the select. Accepts an array or,
   * when set as an attribute, a JSON array string (e.g. distances="[5,10,25]").
   *
   * @type {Array|String}
   */
  distances: {
    type: [Array, String],
    default: () => DEFAULT_DISTANCES,
  },
  /**
   * Offers an "Any Distance" (no-radius) option in the distance select. Absent,
   * a concrete radius is always required (e.g. the Health Plan Directory's
   * required-location flow).
   *
   * @type {Boolean|String}
   */
  allowAny: booleanAttribute(),
  distance: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Which control picks the distance. "select" offers the `distances` option
   * list (plus "Any Distance"); "slider" swaps in an sp-number-slider that
   * snaps through the same `distances` list, matching the Kyruus-style
   * maximum-distance control.
   *
   * @type {String}
   */
  distanceControl: {
    type: String,
    default: "select",
    validator: (value) => ["select", "slider"].includes(value),
  },
  near: {
    type: String,
    default: undefined,
  },
  /**
   * The form element name of the component.
   */
  name: {
    type: String,
    default: "distance",
  },
  /**
   * The label of the activator.
   *
   * @type {String}
   */
  label: {
    type: String,
    default: "Distance",
  },
  url: {
    type: String,
    default: undefined,
  },
  /**
   * The icon to prepend to the activator.
   *
   * @type {String}
   */
  prependIcon: {
    type: String,
    default: "map-pin-filled",
  },
  /**
   * When set, the suggested-location pins render inside a circular background of
   * this colour (e.g. an inverted "violet circle, white pin" treatment). Pair it
   * with the `--sp-comp-distance-filter-list-item-pin-fill-color` token to recolour
   * the pin glyph. Leave undefined for the default bare pin.
   *
   * @type {String}
   */
  locationPinBackgroundColor: {
    type: String,
    default: undefined,
  },
  hidePointer: booleanAttribute(),
  open: booleanAttribute(),
  location: {
    type: String,
    default: undefined,
  },
  inline: booleanAttribute(),
  permanent: booleanAttribute(),
  width: {
    type: String,
    default: undefined,
  },
  closeOnSelect: booleanAttribute(),
  closeOnContentClick: booleanAttribute(),
  /**
   * When set, the filter hides its clear affordances so the location can never
   * be emptied. Used for legally required filters.
   */
  nonClearable: booleanAttribute(),
  /**
   * The title shown at the top of the dropdown content.
   *
   * @type {String}
   */
  dropdownTitle: {
    type: String,
    default: "Near",
  },
  /**
   * The placeholder shown inside the location search input.
   *
   * @type {String}
   */
  searchPlaceholder: {
    type: String,
    default: "City, address, or zip code",
  },
  currentLocationLabel: {
    type: String,
    default: "Use my location",
  },
  currentLocationDisplayText: {
    type: String,
    default: "Current Location",
  },
  currentLocationRetrievingLabel: {
    type: String,
    default: "Retrieving current location",
  },
  currentLocationRetrievalErrorText: {
    type: String,
    default: `An error occurred when trying to detect your current location.
      Please search for your location instead.`,
  },
});
